/**
 * Central configuration for the posture analysis image canvas size.
 * Canvas dimensions are derived from the current browser viewport.
 */
import { useState, useEffect, useCallback, type RefObject } from 'react';

/** Fraction of available viewport dimensions the canvas may use (0.9 = 90%). */
export const CANVAS_SIZE_FACTOR = 0.9;

/** Pixels reserved for sidebar on desktop. On mobile the sidebar is hidden so 0 is used. */
export const RESERVED_VIEWPORT_LEFT = 260;

/** Pixels reserved for header, toolbar, view selector, and padding so canvas fits on screen. */
export const RESERVED_VIEWPORT_TOP = 140;
export const RESERVED_VIEWPORT_TOP_MOBILE = 108;
export const RESERVED_VIEWPORT_BOTTOM = 90;
export const RESERVED_VIEWPORT_BOTTOM_MOBILE = 72;

/** Aspect ratio for posture capture: width / height (3:4). */
export const CANVAS_ASPECT_RATIO = 3 / 4;

/** Compact drop-zone height for self-assessment before the first photo is captured (mobile). */
export const SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_PX = 220;

/** Compact drop-zone height for self-assessment desktop empty / assign-queue states. */
export const SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_DESKTOP_PX = 280;

export interface CanvasDimensions {
  width: number;
  height: number;
}

/**
 * Computes canvas dimensions that fit within CANVAS_SIZE_FACTOR of the *available* viewport
 * (viewport minus sidebar on desktop, header, and chrome) while preserving the posture
 * capture aspect ratio (3:4).
 *
 * @param extraBottomReserved - Additional pixels reserved at bottom (e.g. analysis overlay height).
 * @param rightReservationPx  - Additional pixels reserved on the right (e.g. side-by-side analysis panel).
 * @param maxHeightPx         - Optional hard cap on canvas height (e.g. mobile split view where the
 *                              analysis panel shares the viewport with the Pic).
 * @param topChromeReductionPx - Pixels to subtract from top reservation (e.g. collapsed mobile page header).
 * @param preferFullWidth     - Mobile split: fill container width first, then derive height (matches self-assessment).
 */
export function getCanvasDimensions(
  extraBottomReserved = 0,
  rightReservationPx = 0,
  maxHeightPx?: number,
  topChromeReductionPx = 0,
  extraTopReserved = 0,
  leftReservationPx?: number,
  preferFullWidth = false,
  aspectRatio = CANVAS_ASPECT_RATIO,
): CanvasDimensions {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 };
  }
  // On narrow viewports the sidebar is hidden – don't subtract it from available width.
  const isMobile = window.innerWidth < 768;
  const leftReservation = leftReservationPx !== undefined
    ? leftReservationPx
    : (isMobile ? 0 : RESERVED_VIEWPORT_LEFT);

  const topReserved = Math.max(
    72,
    (isMobile ? RESERVED_VIEWPORT_TOP_MOBILE : RESERVED_VIEWPORT_TOP) - topChromeReductionPx + extraTopReserved,
  );
  const bottomReserved = isMobile ? RESERVED_VIEWPORT_BOTTOM_MOBILE : RESERVED_VIEWPORT_BOTTOM;

  const availableWidth = Math.max(200, window.innerWidth - leftReservation - rightReservationPx);
  let availableHeight = Math.max(200, window.innerHeight - topReserved - bottomReserved - extraBottomReserved);
  // Split-view cap: when the analysis panel shares the viewport (mobile), the Pic must not eat the
  // whole screen. Cap the available height so the canvas leaves room for the analysis below it.
  if (typeof maxHeightPx === 'number' && maxHeightPx > 0) {
    availableHeight = Math.max(160, Math.min(availableHeight, maxHeightPx));
  }

  const maxW = availableWidth * CANVAS_SIZE_FACTOR;
  const maxH = availableHeight * CANVAS_SIZE_FACTOR;
  const aspect = aspectRatio > 0 ? aspectRatio : CANVAS_ASPECT_RATIO;

  if (preferFullWidth) {
    const width = Math.floor(maxW);
    let height = Math.floor(width / aspect);
    if (typeof maxHeightPx === 'number' && maxHeightPx > 0) {
      height = Math.min(height, Math.floor(maxHeightPx));
    }
    return {
      width: Math.max(160, width),
      height: Math.max(120, height),
    };
  }

  let width: number;
  let height: number;
  if (maxW / aspect <= maxH) {
    width = maxW;
    height = maxW / aspect;
  } else {
    height = maxH;
    width = maxH * aspect;
  }
  return { width: Math.floor(width), height: Math.floor(height) };
}

/**
 * Media height that lets a photo fill `containerWidth` without side letterboxing.
 * `aspectRatio` is width / height.
 */
export function fullWidthMediaHeight(containerWidth: number, aspectRatio: number): number {
  if (containerWidth <= 0 || !(aspectRatio > 0)) return 0;
  return Math.max(120, Math.round(containerWidth / aspectRatio));
}

/**
 * Largest width/height that preserves `aspectRatio` (width / height) inside a box,
 * after reserving `chromePx` of vertical space for docked controls.
 */
export function fitMediaFrame(
  boxWidth: number,
  boxHeight: number,
  aspectRatio = CANVAS_ASPECT_RATIO,
  chromePx = 0,
): CanvasDimensions {
  const aspect = aspectRatio > 0 ? aspectRatio : CANVAS_ASPECT_RATIO;
  const availableHeight = Math.max(0, boxHeight - Math.max(0, chromePx));
  if (boxWidth <= 0 || availableHeight <= 0) {
    return { width: 0, height: 0 };
  }
  if (boxWidth / aspect <= availableHeight) {
    return { width: Math.floor(boxWidth), height: Math.floor(boxWidth / aspect) };
  }
  return {
    width: Math.floor(availableHeight * aspect),
    height: Math.floor(availableHeight),
  };
}

/** Tracks an element's content box. No-ops when `enabled` is false. */
export function useObservedBox(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
): CanvasDimensions {
  const [box, setBox] = useState<CanvasDimensions>({ width: 0, height: 0 });

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const publish = (width: number, height: number) => {
      const nextWidth = Math.round(width);
      const nextHeight = Math.round(height);
      setBox((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight },
      );
    };

    publish(el.clientWidth, el.clientHeight);
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      publish(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, ref]);

  return box;
}

/**
 * React hook that returns the current canvas dimensions and updates on window resize.
 *
 * @param extraBottomReserved - Reactive value for additional bottom reservation (e.g. overlay height).
 * @param rightReservationPx  - Reactive value for pixels reserved on the right (e.g. side panel width + gap).
 * @param maxHeightPx         - Reactive hard cap on canvas height (e.g. mobile split view).
 * @param topChromeReductionPx - Reactive reduction when page chrome collapses (e.g. hidden header).
 * @param extraTopReserved    - Reactive value for additional top reservation (e.g. title text height).
 * @param leftReservationPx   - Reactive override for left reservation (e.g. 0 if no sidebar).
 * @param preferFullWidth     - Mobile split width-first sizing (self-assessment parity).
 * @param aspectRatio         - Aspect ratio for capture/display (defaults to CANVAS_ASPECT_RATIO 3:4).
 */
export function useCanvasSize(
  extraBottomReserved = 0,
  rightReservationPx = 0,
  maxHeightPx?: number,
  topChromeReductionPx = 0,
  extraTopReserved = 0,
  leftReservationPx?: number,
  preferFullWidth = false,
  aspectRatio = CANVAS_ASPECT_RATIO,
): CanvasDimensions {
  const [dimensions, setDimensions] = useState<CanvasDimensions>(() =>
    getCanvasDimensions(
      extraBottomReserved,
      rightReservationPx,
      maxHeightPx,
      topChromeReductionPx,
      extraTopReserved,
      leftReservationPx,
      preferFullWidth,
      aspectRatio,
    ),
  );

  const updateDimensions = useCallback(() => {
    setDimensions(
      getCanvasDimensions(
        extraBottomReserved,
        rightReservationPx,
        maxHeightPx,
        topChromeReductionPx,
        extraTopReserved,
        leftReservationPx,
        preferFullWidth,
        aspectRatio,
      ),
    );
  }, [
    extraBottomReserved,
    rightReservationPx,
    maxHeightPx,
    topChromeReductionPx,
    extraTopReserved,
    leftReservationPx,
    preferFullWidth,
    aspectRatio,
  ]);

  useEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [updateDimensions]);

  return dimensions;
}
